/**
 * Relay — Offline Cache Utility
 *
 * Uses IndexedDB to store:
 * - Active ticket data when offline
 * - Fault code lookup table (rules-based triage fallback)
 * - Pre-cached evidence step templates
 * - Coaching FAQ cache
 *
 * Handles connectivity detection and sync queue management.
 */

const DB_NAME = 'fieldassist_offline';
const DB_VERSION = 2;

// --- Database Setup ---

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains('offline_tickets')) {
        const ticketStore = db.createObjectStore('offline_tickets', { keyPath: 'ticket_id' });
        ticketStore.createIndex('status', 'status', { unique: false });
      }

      if (!db.objectStoreNames.contains('fault_codes')) {
        db.createObjectStore('fault_codes', { keyPath: 'code' });
      }

      if (!db.objectStoreNames.contains('evidence_templates')) {
        db.createObjectStore('evidence_templates', { keyPath: 'category' });
      }

      if (!db.objectStoreNames.contains('coaching_faq')) {
        db.createObjectStore('coaching_faq', { keyPath: 'key' });
      }

      if (!db.objectStoreNames.contains('offline_evidence')) {
        const evidenceStore = db.createObjectStore('offline_evidence', { keyPath: 'ticket_id' });
        evidenceStore.createIndex('synced', 'synced', { unique: false });
      }

      if (!db.objectStoreNames.contains('field_requests')) {
        const reqStore = db.createObjectStore('field_requests', { keyPath: 'ticket_id' });
        reqStore.createIndex('created_at', 'created_at', { unique: false });
      }
    };
  });
}

function txPromise(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
  });
}

// --- Offline Ticket Management ---

export async function saveOfflineTicket(ticket) {
  const db = await openDB();
  const tx = db.transaction('offline_tickets', 'readwrite');
  const store = tx.objectStore('offline_tickets');

  const offlineTicket = {
    ...ticket,
    ticket_id: `OFFLINE-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    created_at: new Date().toISOString(),
    status: 'pending_sync',
    offline_created: true,
  };

  store.put(offlineTicket);
  await txPromise(tx);
  return offlineTicket;
}

export async function getOfflineTickets() {
  const db = await openDB();
  const tx = db.transaction('offline_tickets', 'readonly');
  const store = tx.objectStore('offline_tickets');
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getPendingSyncTickets() {
  const db = await openDB();
  const tx = db.transaction('offline_tickets', 'readonly');
  const store = tx.objectStore('offline_tickets');
  const index = store.index('status');
  return new Promise((resolve, reject) => {
    const request = index.getAll('pending_sync');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function markTicketSynced(ticketId) {
  const db = await openDB();
  const tx = db.transaction('offline_tickets', 'readwrite');
  const store = tx.objectStore('offline_tickets');
  return new Promise((resolve, reject) => {
    const getReq = store.get(ticketId);
    getReq.onsuccess = () => {
      const ticket = getReq.result;
      if (ticket) {
        ticket.status = 'synced';
        ticket.synced_at = new Date().toISOString();
        store.put(ticket);
      }
      resolve();
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

// --- Offline Evidence Storage ---

export async function saveOfflineEvidence(ticketId, evidenceResults) {
  const db = await openDB();
  const tx = db.transaction('offline_evidence', 'readwrite');
  const store = tx.objectStore('offline_evidence');
  store.put({
    ticket_id: ticketId,
    results: evidenceResults,
    collected_at: new Date().toISOString(),
    synced: false,
  });
  return txPromise(tx);
}

export async function getPendingOfflineEvidence() {
  const db = await openDB();
  const tx = db.transaction('offline_evidence', 'readonly');
  const store = tx.objectStore('offline_evidence');
  const index = store.index('synced');
  return new Promise((resolve, reject) => {
    const request = index.getAll(false);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

export async function markEvidenceSynced(ticketId) {
  const db = await openDB();
  const tx = db.transaction('offline_evidence', 'readwrite');
  const store = tx.objectStore('offline_evidence');
  return new Promise((resolve, reject) => {
    const getReq = store.get(ticketId);
    getReq.onsuccess = () => {
      const record = getReq.result;
      if (record) {
        record.synced = true;
        store.put(record);
      }
      resolve();
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

// --- Field Engineer Request History (Dashboard) ---

export async function saveFieldRequest(request) {
  const db = await openDB();
  const tx = db.transaction('field_requests', 'readwrite');
  const store = tx.objectStore('field_requests');
  const now = new Date().toISOString();
  const record = {
    ...request,
    updated_at: now,
    created_at: request.created_at || now,
  };
  store.put(record);
  return txPromise(tx).then(() => record);
}

export async function updateFieldRequestStatus(ticketId, status, extra = {}) {
  const db = await openDB();
  const tx = db.transaction('field_requests', 'readwrite');
  const store = tx.objectStore('field_requests');
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    const getReq = store.get(ticketId);
    getReq.onsuccess = () => {
      const existing = getReq.result;
      if (existing) {
        const updated = { ...existing, status, updated_at: new Date().toISOString(), ...extra };
        store.put(updated);
      }
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

export async function getFieldRequest(ticketId) {
  const db = await openDB();
  const tx = db.transaction('field_requests', 'readonly');
  const store = tx.objectStore('field_requests');
  return new Promise((resolve, reject) => {
    const request = store.get(ticketId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllFieldRequests() {
  const db = await openDB();
  const tx = db.transaction('field_requests', 'readonly');
  const store = tx.objectStore('field_requests');
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const list = request.result || [];
      list.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      resolve(list);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function deleteFieldRequest(ticketId) {
  const db = await openDB();
  const tx = db.transaction('field_requests', 'readwrite');
  const store = tx.objectStore('field_requests');
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    store.delete(ticketId);
  });
}

// --- Fault Code Lookup (Offline Triage Fallback) ---

const FAULT_CODE_MAP = {
  'P02': { category: 'cooling_system', risk: 'MEDIUM' },
  'P01': { category: 'sensor_fault', risk: 'LOW' },
  'P05': { category: 'electrical', risk: 'LOW' },
  'P06': { category: 'electrical', risk: 'MEDIUM' },
  'P00': { category: 'fuel_system', risk: 'MEDIUM' },
  'P03': { category: 'mechanical', risk: 'HIGH' },
  'P20': { category: 'exhaust_aftertreatment', risk: 'MEDIUM' },
  'P52': { category: 'lubrication', risk: 'HIGH' },
};

/**
 * Offline rules-based triage. Risk is LOW | MEDIUM | HIGH only.
 * Unknown fault prefix → MEDIUM; danger words in symptoms upgrade LOW → MEDIUM.
 * See docs/risk_classification.md.
 */
export function offlineTriage(faultCode, symptoms = '') {
  const prefix = faultCode.substring(0, 3);
  const match = FAULT_CODE_MAP[prefix] || { category: 'unknown', risk: 'MEDIUM' };

  const dangerWords = ['smoke', 'fire', 'leak', 'steam', 'overheating', 'stall'];
  let risk = match.risk;
  if (dangerWords.some(word => symptoms.toLowerCase().includes(word))) {
    if (risk === 'LOW') risk = 'MEDIUM';
  }

  return {
    category: match.category,
    risk_level: risk,
    confidence: 0.50,
    reasoning: `Offline rules-based: ${faultCode} → ${match.category}`,
    next_diagnostic_area: `Investigate ${match.category.replace('_', ' ')} components`,
    requires_escalation: true,
    offline: true,
  };
}

// --- Connectivity Detection ---

let isOnline = true;
let heartbeatInterval = null;
let initialCheckTimer = null;
let consecutiveFailures = 0;
let _syncListeners = [];
const HEARTBEAT_URL = '/api/health';
const HEARTBEAT_INTERVAL_MS = 30000;
const INITIAL_CHECK_DELAY_MS = 1500;
const FAILURES_BEFORE_OFFLINE = 2;

function createTimeoutSignal(ms) {
  if (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) {
    return AbortSignal.timeout(ms);
  }
  const c = new AbortController();
  setTimeout(() => c.abort(), ms);
  return c.signal;
}

async function checkHealth() {
  try {
    const response = await fetch(HEARTBEAT_URL, {
      method: 'GET',
      cache: 'no-store',
      signal: createTimeoutSignal(10000),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export function getConnectionStatus() {
  return isOnline;
}

export function onSyncEvent(listener) {
  _syncListeners.push(listener);
  return () => {
    _syncListeners = _syncListeners.filter((l) => l !== listener);
  };
}

function _notifySync(event) {
  _syncListeners.forEach((fn) => {
    try { fn(event); } catch (e) { console.error('Sync listener error:', e); }
  });
}

export async function recheckConnectivity(onStatusChange) {
  const ok = await checkHealth();
  if (ok) {
    consecutiveFailures = 0;
    isOnline = true;
    onStatusChange(true);
    syncPendingTickets();
  }
  return ok;
}

export function startConnectivityMonitor(onStatusChange) {
  const runInitialCheck = () => {
    checkHealth().then((ok) => {
      if (ok) {
        consecutiveFailures = 0;
        isOnline = true;
        onStatusChange(true);
        syncPendingTickets();
      }
    });
  };
  initialCheckTimer = setTimeout(runInitialCheck, INITIAL_CHECK_DELAY_MS);

  window.addEventListener('online', () => {
    consecutiveFailures = 0;
    isOnline = true;
    onStatusChange(true);
    syncPendingTickets();
  });

  window.addEventListener('offline', () => {
    isOnline = false;
    onStatusChange(false);
  });

  heartbeatInterval = setInterval(async () => {
    const ok = await checkHealth();
    if (ok) {
      consecutiveFailures = 0;
      if (!isOnline) {
        isOnline = true;
        onStatusChange(true);
        syncPendingTickets();
      }
    } else {
      consecutiveFailures += 1;
      if (consecutiveFailures >= FAILURES_BEFORE_OFFLINE && isOnline) {
        isOnline = false;
        onStatusChange(false);
      }
    }
  }, HEARTBEAT_INTERVAL_MS);
}

export function stopConnectivityMonitor() {
  if (initialCheckTimer) {
    clearTimeout(initialCheckTimer);
    initialCheckTimer = null;
  }
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

// --- Sync Logic ---

let _syncing = false;

export async function syncPendingTickets() {
  if (_syncing) return { synced: 0, conflicts: [] };
  _syncing = true;

  try {
    const pending = await getPendingSyncTickets();
    if (pending.length === 0) {
      _syncing = false;
      return { synced: 0, conflicts: [] };
    }

    _notifySync({ type: 'start', count: pending.length });

    const response = await fetch('/api/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tickets: pending.map(t => ({
          technician_id: t.technician_id,
          technician_name: t.technician_name,
          equipment_model: t.equipment_model,
          equipment_type: t.equipment_type,
          model_year: t.model_year,
          operating_hours: t.operating_hours,
          fault_code: t.fault_code,
          symptoms: t.symptoms,
          offline_created: true,
        })),
      }),
    });

    const result = await response.json();
    const serverIds = result.ticket_ids || [];

    for (let i = 0; i < pending.length; i++) {
      const offlineId = pending[i].ticket_id;
      const serverId = serverIds[i];
      await markTicketSynced(offlineId);

      if (serverId) {
        const existing = await getFieldRequest(offlineId);
        if (existing) {
          await deleteFieldRequest(offlineId);
          await saveFieldRequest({
            ...existing,
            ticket_id: serverId,
            offline_ticket_id: offlineId,
            status: 'escalated',
            synced_at: new Date().toISOString(),
          });
        }
      } else {
        await updateFieldRequestStatus(offlineId, 'synced', {
          synced_at: new Date().toISOString(),
        }).catch(() => {});
      }
    }

    _notifySync({
      type: 'complete',
      synced: result.synced_count || pending.length,
      conflicts: result.conflicts || [],
    });

    return result;
  } catch (error) {
    console.error('Sync failed:', error);
    _notifySync({ type: 'error', error: error.message });
    return { synced: 0, conflicts: [], error: error.message };
  } finally {
    _syncing = false;
  }
}

// --- Cache Seeding (call on first online load) ---

export async function seedOfflineCache(faultCodes, evidenceTemplates, coachingFAQ) {
  const db = await openDB();

  if (faultCodes) {
    const tx = db.transaction('fault_codes', 'readwrite');
    const store = tx.objectStore('fault_codes');
    for (const code of faultCodes) {
      store.put(code);
    }
  }

  if (evidenceTemplates) {
    const tx = db.transaction('evidence_templates', 'readwrite');
    const store = tx.objectStore('evidence_templates');
    for (const template of evidenceTemplates) {
      store.put(template);
    }
  }

  if (coachingFAQ) {
    const tx = db.transaction('coaching_faq', 'readwrite');
    const store = tx.objectStore('coaching_faq');
    for (const entry of coachingFAQ) {
      store.put(entry);
    }
  }
}
